import IconButton from '@mui/material/IconButton';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AppContext } from '../../context/app';
import { useNetworkManager } from '../../hooks/useNetworkManager';
import { ROUTES } from '../../utils/routes';
import ButtonStyled from '../shared/button/Button';
import { CallToActionButton } from '../shared/callToActionButton/CallToActionButton';
import Icon from '../shared/icon/Icon';
import { IconType } from '../shared/icon/types';
import { Modal } from '../shared/modal/Modal';
import styles from './tutorialModal.module.scss';

export const Tutorial: React.FC = () => {
  const { t } = useTranslation('common');
  const { tutorialStep, setTutorialStep } = useContext(AppContext);
  const navigate = useNavigate();
  const location = window.location.pathname;
  const { brandingData, siteName } = useNetworkManager();

  const setTutorialCompleted = (isCompleted: boolean) => {
    try {
      localStorage.setItem('tutorialCompleted', JSON.stringify(isCompleted));
    } catch (error) {
      console.error('Error writing to localStorage', error);
    }
  };

  const isTutorialCompleted = () => {
    try {
      const status = localStorage.getItem('tutorialCompleted');
      return status ? JSON.parse(status) : false;
    } catch (error) {
      console.error('Error reading from localStorage', error);
      return false;
    }
  };

  const [showModal, setShowModal] = useState(!isTutorialCompleted());

  const handleStartTutorial = () => {
    setTutorialCompleted(true);
    setShowModal(false);
    setTutorialStep(1);
    if (location !== ROUTES.CAMPUS_NAVIGATION) {
      navigate({ pathname: ROUTES.CAMPUS_NAVIGATION });
    }
  };

  const handleSkipTutorial = () => {
    setTutorialCompleted(true);
    setShowModal(false);
  };

  const handleStep1Next = () => {
    setTutorialStep(2);
    if (location !== ROUTES.CAMPUS_DIRECTORY) {
      navigate({ pathname: ROUTES.CAMPUS_DIRECTORY });
    }
  };

  const handleStep2Previous = () => {
    setTutorialStep(1);
    navigate({ pathname: ROUTES.CAMPUS_NAVIGATION });
  };

  const handleStep2Next = () => {
    setTutorialStep(3);
    navigate({ pathname: ROUTES.CAMPUS_NAVIGATION });
  };

  const handleStep3Previous = () => {
    setTutorialStep(2);
    navigate({ pathname: ROUTES.CAMPUS_DIRECTORY });
  };

  const handleCompleteTutorial = () => {
    setTutorialStep(0);
  };

  return (
    <>
      {showModal && siteName && (
        <Modal isOpen={true}>
          <div className={styles.container}>
            <img src="/images/map.png" alt="Tutorial" />
            <span className={styles.title}>
              {t('tutorial.title', { siteName })}
            </span>
            <span className={styles.subtitle}>{t('tutorial.subtitle')}</span>
            <div className={styles.buttons}>
              <ButtonStyled
                onClick={handleSkipTutorial}
                variant="outlined"
                flexWide={true}
              >
                {t('tutorial.cancel')}
              </ButtonStyled>
              <ButtonStyled onClick={handleStartTutorial} flexWide={true}>
                {t('tutorial.view')}
              </ButtonStyled>
            </div>
          </div>
        </Modal>
      )}

      {tutorialStep === 1 && location === ROUTES.CAMPUS_NAVIGATION && (
        <div className={styles.stepContainer}>
          <div className={styles.step}>
            <div className={styles.topRow}>
              <div className={styles.stepNumber}>1 {t('tutorial.of')} 3</div>
              <IconButton onClick={handleCompleteTutorial} aria-label="Close">
                <Icon
                  icon={IconType.CLOSE}
                  size={20}
                  ariaLabel="Close"
                  ariaRole="button"
                />
              </IconButton>
            </div>
            <div className={styles.stepTitle}>{t('tutorial.step1.title')}</div>
            <div className={styles.stepDescription}>
              {t('tutorial.step1.description1', { siteName })}
            </div>
            <div className={styles.stepDescription}>
              {t('tutorial.step1.description2')}
            </div>
            <div className={styles.stepDescription}>
              {t('tutorial.step1.description3')
                .split('\n')
                .map((line: string, index: number) => (
                  <React.Fragment key={index}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
            </div>
            <div className={styles.buttonRow}>
              <div className={styles.buttonSpacer}></div>
              <ButtonStyled onClick={handleStep1Next}>
                {t('tutorial.next')}
              </ButtonStyled>
            </div>
          </div>
        </div>
      )}

      {tutorialStep === 2 && location === ROUTES.CAMPUS_DIRECTORY && (
        <div className={styles.stepContainer}>
          <div className={styles.step}>
            <div className={styles.topRow}>
              <div className={styles.stepNumber}>2 {t('tutorial.of')} 3</div>
              <IconButton onClick={handleCompleteTutorial} aria-label="Close">
                <Icon
                  icon={IconType.CLOSE}
                  size={20}
                  ariaLabel="Close"
                  ariaRole="button"
                />
              </IconButton>
            </div>
            <div className={styles.stepTitle}>{t('tutorial.step2.title')}</div>
            <div className={styles.stepDescription}>
              {t('tutorial.step2.description1', { siteName })}
            </div>
            <div className={styles.stepDescription}>
              {t('tutorial.step2.description2')}
            </div>
            <div className={styles.stepDescription}>
              {t('tutorial.step2.description3')
                .split('\n')
                .map((line: string, index: number) => (
                  <React.Fragment key={index}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
            </div>
            <div className={styles.buttonRow}>
              <ButtonStyled onClick={handleStep2Previous} variant={'outlined'}>
                {t('tutorial.previous')}
              </ButtonStyled>
              <ButtonStyled onClick={handleStep2Next}>
                {t('tutorial.next')}
              </ButtonStyled>
            </div>
          </div>
        </div>
      )}

      {tutorialStep === 3 && (
        <div className={styles.stepContainer}>
          <div className={styles.step}>
            <div className={styles.topRow}>
              <div className={styles.stepNumber}>3 {t('tutorial.of')} 3</div>
              <IconButton onClick={handleCompleteTutorial} aria-label="Close">
                <Icon
                  icon={IconType.CLOSE}
                  size={20}
                  ariaLabel="Close"
                  ariaRole="button"
                />
              </IconButton>
            </div>

            <div className={styles.stepTitle}>{t('tutorial.step3.title')}</div>
            <div className={styles.imageAndDescription}>
              {brandingData && (
                <img
                  className={styles.promoImage}
                  src={brandingData[0].install_app_cta_image}
                  alt="App Preview"
                />
              )}
              <div className={styles.stepDescriptionCol}>
                <div className={styles.stepDescription}>
                  {t('tutorial.step3.description', { siteName })}
                </div>
                <CallToActionButton
                  useTextButton={true}
                  origin="TutorialModal"
                />
              </div>
            </div>
            <div className={styles.buttonRow}>
              <ButtonStyled onClick={handleStep3Previous} variant={'outlined'}>
                {t('tutorial.previous')}
              </ButtonStyled>
              <ButtonStyled onClick={handleCompleteTutorial}>
                {t('tutorial.done')}
              </ButtonStyled>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
